import React, { useState, useEffect } from 'react';
import { ChevronDown, Phone } from 'lucide-react';
import { 
  COUNTRY_CODES, 
  formatToE164, 
  formatPhoneForDisplay, 
  validatePhone, 
  detectCountryCode,
  PhoneValidationResult 
} from '../../utils/phoneValidation';

interface PhoneInputProps {
  value: string;
  onChange: (value: string, validation: PhoneValidationResult) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: string;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  placeholder = "Enter phone number",
  className = "",
  disabled = false,
  error
}) => {
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [displayValue, setDisplayValue] = useState('');
  const [validationResult, setValidationResult] = useState<PhoneValidationResult>({
    isValid: true,
    formatted: ''
  });

  // Initialize country code and display value from prop value
  useEffect(() => {
    if (value) {
      const detectedCode = detectCountryCode(value);
      const country = COUNTRY_CODES.find(c => c.code === detectedCode) || COUNTRY_CODES[0];
      setSelectedCountry(country);
      
      // Set display value (remove country code for input)
      if (value.startsWith(detectedCode)) {
        setDisplayValue(value.slice(detectedCode.length));
      } else {
        setDisplayValue(value);
      }
    }
  }, [value]);

  const handleInputChange = (inputValue: string) => {
    // Remove non-digit characters for cleaner input
    const cleaned = inputValue.replace(/[^\d]/g, '');
    setDisplayValue(cleaned);
    
    // Format to E.164 and validate
    const fullNumber = selectedCountry.code + cleaned;
    const validation = validatePhone(fullNumber, selectedCountry.code);
    
    setValidationResult(validation);
    onChange(validation.formatted, validation);
  };

  const handleCountryChange = (country: typeof COUNTRY_CODES[0]) => {
    setSelectedCountry(country);
    setShowDropdown(false);
    
    // Re-validate with new country code
    if (displayValue) {
      const fullNumber = country.code + displayValue;
      const validation = validatePhone(fullNumber, country.code);
      setValidationResult(validation);
      onChange(validation.formatted, validation);
    }
  };

  const inputClasses = `
    flex-1 px-3 py-2 border-0 focus:outline-none focus:ring-0 bg-transparent
    ${disabled ? 'cursor-not-allowed opacity-50' : ''}
  `;

  const containerClasses = `
    flex items-center w-full border rounded-lg focus-within:ring-2 focus-within:ring-primary/50 transition-all
    ${error || (!validationResult.isValid && displayValue) ? 'border-red-300 focus-within:ring-red-500/50' : 'border-border'}
    ${disabled ? 'bg-muted/50 cursor-not-allowed' : 'bg-background'}
    ${className}
  `;

  return (
    <div className="space-y-2">
      <div className={containerClasses}>
        {/* Country Code Dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => !disabled && setShowDropdown(!showDropdown)}
            disabled={disabled}
            className="flex items-center space-x-2 px-3 py-2 hover:bg-muted/50 transition-colors rounded-l-lg border-r border-border"
          >
            <span className="text-lg">{selectedCountry.flag}</span>
            <span className="text-sm font-medium">{selectedCountry.code}</span>
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </button>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div className="absolute top-full left-0 z-50 mt-1 w-64 bg-background border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {COUNTRY_CODES.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountryChange(country)}
                  className="w-full flex items-center space-x-3 px-3 py-2 hover:bg-muted/50 transition-colors text-left"
                >
                  <span className="text-lg">{country.flag}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{country.code}</span>
                      <span className="text-sm text-muted-foreground truncate">
                        {country.name}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <div className="flex items-center flex-1">
          <Phone className="w-4 h-4 text-muted-foreground ml-3" />
          <input
            type="tel"
            value={displayValue}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className={inputClasses}
          />
        </div>
      </div>

      {/* Validation Message */}
      {(error || (!validationResult.isValid && displayValue)) && (
        <p className="text-red-600 text-sm">
          {error || validationResult.error}
        </p>
      )}

      {/* Format Preview */}
      {validationResult.isValid && validationResult.formatted && (
        <p className="text-xs text-muted-foreground">
          Format: {formatPhoneForDisplay(validationResult.formatted)}
        </p>
      )}

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};
